import type { PropertyMap } from '@dynatrace/dtrum-api-types';

import { ENVS, getEnv } from '@adsk/acs-sc-web-platform-api';

import { MetadataType } from './monitoring';

// This function is a temporary solution to send properties to dynatrace, once we have final solution from dynatrace it will be deprecated.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function categorizeProperties(input: PropertyMap<any>): {
  integerNumber?: PropertyMap<number>;
  date?: PropertyMap<Date>;
  shortString?: PropertyMap<string>;
  doubleNumber?: PropertyMap<number>;
} {
  const integerNumber: PropertyMap<number> = {};
  const date: PropertyMap<Date> = {};
  const shortString: PropertyMap<string> = {};
  const doubleNumber: PropertyMap<number> = {};

  Object.entries(input).forEach(([key, value]) => {
    const resolvedValue =
      typeof value === 'object' && value !== null && 'value' in value
        ? value.value
        : value;

    if (typeof resolvedValue === 'number') {
      // Distinguish between integer and floating-point (doubleNumber) since dynatrace API separate them
      if (Number.isInteger(resolvedValue)) {
        integerNumber[key] = value;
      } else {
        doubleNumber[key] = value;
      }
    } else if (resolvedValue instanceof Date) {
      date[key] = value;
    } else if (typeof resolvedValue === 'string') {
      shortString[key] = value;
    }
  });

  return { integerNumber, date, shortString, doubleNumber };
}

function wrapWithScope(data: unknown, scope?: string): Error | string {
  if (typeof data === 'string') {
    return `[${scope || 'UNSCOPED'}] - ${data}`;
  }

  const newError = new Error(
    `[${scope || 'UNSCOPED'}] - ${(data as Error).message}`,
  );
  newError.stack = (data as Error).stack; // Preserve stack trace
  newError.name = (data as Error).name; // Preserve error name
  Object.assign(newError, data); // Copy custom properties
  Object.setPrototypeOf(newError, Object.getPrototypeOf(data)); // Preserve prototype chain

  return newError;
}

//This function is used to spread the scope property value to higher level since dynatrace doesn't support nested object
function spreadScopedPropertiesForRootLevel(metadata: MetadataType) {
  if (!metadata.scope) {
    return metadata;
  }

  const scope = metadata.scope;
  const scopeProperties = metadata?.[scope] || {};

  // We add custom property with scope as a key in report api - https://git.autodesk.com/BIM360/acs-sc-web-platform/blob/master/libs/exo-skeleton-api/monitoring.ts#L77
  // We need to spread the scope property value to higher level since dynatrace doesn't support nested object
  const dynatraceMetadata: MetadataType = {
    ...metadata,
    ...scopeProperties,
  };
  // We need to remove scope field from metadata to avoid duplication once they will support objects (currently doesn't impact anything)
  if (scope && metadata.hasOwnProperty(scope)) {
    delete dynatraceMetadata[scope]; //:TODO: Remove scope field after spread value, currently don't work
  }
  return dynatraceMetadata;
}

export function sendEventToDynatrace({
  data,
  scope,
  metadata = {},
  isError = false,
}: {
  data: string | Error;
  scope?: string;
  metadata?: MetadataType;
  isError?: boolean;
}) {
  try {
    const env = getEnv();

    if (env !== ENVS.QA) {
      return;
    }

    const dtMonitorApi =
      (typeof window !== 'undefined' && window.dtrum) || null;
    if (!dtMonitorApi) {
      return;
    }

    // https://www.dynatrace.com/support/doc/javascriptapi/interfaces/dtrum_types.DtrumApi.html#enterAction
    const wrappedData = wrapWithScope(data, scope);

    const actionId = dtMonitorApi.enterAction(
      typeof wrappedData === 'string' ? wrappedData : wrappedData.message,
      '',
      Date.now(),
      window.location.href,
    );

    const dynatraceMetadata: MetadataType =
      spreadScopedPropertiesForRootLevel(metadata);

    const { integerNumber, date, shortString, doubleNumber } =
      categorizeProperties(dynatraceMetadata);

    dtMonitorApi.addActionProperties(
      actionId,
      integerNumber,
      date,
      shortString,
      doubleNumber,
    );

    if (isError) {
      dtMonitorApi.reportError(wrappedData, actionId);
    }
    dtMonitorApi.leaveAction(actionId);
  } catch (e) {
    console.error(`Failed to send event to Dynatrace: ${e}`);
  }
}
